import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { createJob, fetchJobs } from "../../../redux/actions/jobActions";
import {
  FiX,
  FiCheck,
  FiPlusCircle,
  FiType,
  FiBriefcase,
  FiMapPin,
  FiClock,
  FiFileText,
  FiDollarSign,
  FiAward,
  FiCheckCircle,
} from "react-icons/fi";

const CreateJobs = ({ close }: any) => {
  const dispatch = useDispatch<any>();

  const [formData, setFormData] = useState({
    job_title: "",
    department: "",
    location: "",
    job_type: "Full Time",
    experience_required: "",
    salary_range: "",
    description: "",
    requirements: "",
    status: "active",
  });

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    
    // Using simple JSON based on your API description
    const success = await dispatch(createJob(formData));
    if (success) {
      dispatch(fetchJobs());
      close();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-6 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-white">
              <div className="p-2 bg-white/20 rounded-lg">
                <FiPlusCircle className="text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Post New Job</h2>
                <p className="text-blue-100 text-sm">
                  Add details to attract the best talent for EBLUESYS
                </p>
              </div>
            </div>
            <button
              onClick={close}
              className="text-white/80 hover:text-white transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Row 1: Job Title & Department */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiType /> Job Title *
              </label>
              <input
                name="job_title"
                required
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Senior Backend Developer"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiBriefcase /> Department *
              </label>
              <input
                name="department"
                required
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Engineering"
              />
            </div>
          </div>

          {/* Row 2: Location, Type & Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiMapPin /> Location *
              </label>
              <input
                name="location"
                required
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none"
                placeholder="Remote / On-site"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiClock /> Job Type
              </label>
              <select
                name="job_type"
                value={formData.job_type}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none cursor-pointer"
              >
                <option value="Full Time">Full Time</option>
                <option value="Part Time">Part Time</option>
                <option value="Contract">Contract</option>
                <option value="Internship">Internship</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiCheckCircle /> Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none cursor-pointer"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Row 3: Experience & Salary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiAward /> Experience Required *
              </label>
              <input
                name="experience_required"
                required
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none"
                placeholder="e.g. 2-4 Years"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiDollarSign /> Salary Range
              </label>
              <input
                name="salary_range"
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none"
                placeholder="e.g. 5-8 LPA"
              />
            </div>
          </div>

          {/* Row 4: Description */}
          <div className="space-y-1">
            <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
              <FiFileText /> Job Description *
            </label>
            <textarea
              name="description"
              rows={4}
              required
              onChange={handleChange}
              className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Describe the roles and responsibilities..."
            />
          </div>

          {/* Row 5: Requirements */}
          <div className="space-y-1">
            <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
              <FiCheckCircle /> Requirements *
            </label>
            <textarea
              name="requirements"
              rows={4}
              required
              onChange={handleChange}
              className="w-full p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm resize-none"
              placeholder="Skills, qualifications, and specific tools required..."
            />
          </div>

          {/* Footer Actions */}
          <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={close}
              className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-3 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
            >
              Cancel
            </button>

            {formData.job_title &&
            formData.department &&
            formData.location &&
            formData.description ? (
              <motion.button
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                whileHover={{
                  scale: 1.02,
                  shadow: "0 20px 25px -5px rgb(0 0 0 / 0.1)",
                }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-bold shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2 transition-all cursor-pointer"
              >
                <FiCheck className="text-lg" />
                Publish Job
              </motion.button>
            ) : (
              <div className="flex-1 bg-gray-100 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 py-3 rounded-xl font-bold flex items-center justify-center gap-2 border border-dashed border-gray-300 dark:border-gray-600">
                Fill required fields to publish
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateJobs;